import { memo, useCallback, useMemo, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  closestCorners,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { TriangleAlert } from 'lucide-react';
import type { BoardColumn } from '@rundown/types';
import { EmptyState, Pill } from '../../components';
import { formatHours } from '../../lib/format';
import { cx } from '../../lib/cx';
import { useCapability } from '../workspace/useCapability';
import { boardQueryOptions, type BoardCard, type BoardLane, type BoardSprint } from '../board/api';
import { useBoardMove } from '../board/useBoardMove';
import { RequestMenu } from '../requests/RequestMenu';
import {
  BOARD_COLUMNS,
  COLUMN_LABELS,
  LANES,
  LANE_LABELS,
  PRIORITY_LABELS,
  PRIORITY_TONES,
} from '../board/labels';
import { TIMELINE_STATE_LABEL, sprintRange } from '../sprints/sprintMeta';

/**
 * Delivery — the Plan screen's second layout: six columns (BOARD_COLUMNS,
 * which mirrors `requests_board_column_valid` in db/schema/work.ts) crossed
 * with two bands, the running sprint and the hotfixes eating into it. One cell
 * per (lane, column) pair; a card lives in exactly one cell and drags freely
 * between any two.
 *
 * ── Two bands, not three ────────────────────────────────────────────────────
 * There was an `unscheduled` band holding everything else settled in the
 * workspace. It went because it was three screens at once: the Backlog already
 * ranks that set, its Triage column was a second Home › Review, and 227 cards
 * of it was what made dragging anything anywhere slow (see below). The part of
 * it that mattered — work under way that no sprint committed to — is the strip
 * at the bottom, where a number that should be zero belongs.
 *
 * ── Bands, not rows ─────────────────────────────────────────────────────────
 * Each lane is its own bordered, tinted band with its name and count in the
 * band header, rather than a label in a seventh grid column. The two lanes
 * answer different questions — what we committed to this week, what is eating
 * the reserve — and as undifferentiated rows of one grid they read as one long
 * list. Both bands repeat the same `gridTemplateColumns`, so the six columns
 * line up across them.
 *
 * ── Why the cells scroll and Done is bounded ────────────────────────────────
 * A cell scrolls inside its band (CELL_MAX_HEIGHT) instead of growing the
 * page. Before this, one tall column stretched its whole band and the page
 * scrolled for as long as the workspace had history — which the Done column
 * guaranteed, since the server sent every request ever finished. The server
 * now sends only recently-closed work (DONE_VISIBLE_DAYS in board.service.ts)
 * and the Done header links to Completed for the rest. That bound is also a
 * drag performance fix: every draggable card here is a dnd-kit sortable, and
 * dnd-kit re-renders each one as the pointer moves, so the cost of a drag was
 * proportional to the workspace's entire finished history.
 *
 * ── Multi-container drag, briefly ───────────────────────────────────────────
 * Unlike BacklogPage's single sortable list, a card here can move between 12
 * independently-ordered lists. Each cell is registered as BOTH a `useDroppable`
 * target (id `cell::lane::column`, so a drop into an EMPTY cell or the empty
 * space below a cell's last card has somewhere to land) AND a `SortableContext`
 * over its own cards (so reordering within a populated cell works the way
 * BacklogPage's single list does) — the two ids share a namespace on purpose,
 * which is the shape dnd-kit's own "multiple containers" pattern uses.
 * `onDragEnd` below is the only handler; there is no `onDragOver`
 * cross-container live-reflow, so a card does not visually jump to the hovered
 * column until the drop completes and the optimistic mutation applies — a
 * deliberate simplification against the full kanban reference implementation's
 * real-time-while-dragging reflow, noted here rather than silently shipped as
 * equivalent.
 */

/** Tall enough for roughly six cards; past that the cell scrolls on its own. */
const CELL_MAX_HEIGHT = '32rem';

const GRID_COLUMNS = `repeat(${BOARD_COLUMNS.length}, minmax(13.125rem, 1fr))`;

/**
 * Each lane's own colour. The tint is the band, not the cards: cards stay white
 * so they read the same wherever they sit, and the surface behind them is what
 * says which lane you are looking at. Hotfixes get the danger tint because a
 * hotfix band that is filling up is the thing this screen most needs to make
 * impossible to miss (v0.3 §1: "ad-hoc bugs are never weighed against the
 * plan").
 */
const LANE_TONES: Readonly<Record<BoardLane, { band: string; chip: string }>> = {
  sprint: {
    band: 'border-accent-border bg-accent-tint-soft',
    chip: 'bg-accent text-white',
  },
  hotfix: {
    band: 'border-danger-border bg-danger-tint',
    chip: 'bg-danger text-white',
  },
};

function cellId(lane: BoardLane, column: BoardColumn): string {
  return `cell::${lane}::${column}`;
}

/** The card's contents, shared by both card shapes below so a read-only board
 * and an editable one cannot drift into looking different. */
function CardFace({
  card,
  workspaceId,
}: {
  readonly card: BoardCard;
  readonly workspaceId: string;
}): ReactElement {
  const priorityTone = PRIORITY_TONES[card.priority];

  return (
    <>
      <div className="flex flex-wrap items-center gap-1">
        <span className="fs-10.5 whitespace-nowrap text-muted">#{card.number}</span>
        {priorityTone !== null && <Pill tone={priorityTone}>{PRIORITY_LABELS[card.priority]}</Pill>}
        {card.needsReview && <Pill tone="warn">Needs review</Pill>}
        {!card.visibleToClient && <Pill tone="violet">Internal</Pill>}
        {/* The menu, not only the drag. Reaching this board at all requires
            `board.write` (BoardController gates the whole controller), so
            there is no capability check here — and a drag across six columns
            on a narrow screen is a worse way to say "this is done" than
            picking it from a list. */}
        <RequestMenu
          workspaceId={workspaceId}
          requestId={card.id}
          number={card.number}
          column={card.boardColumn}
          className="ml-auto"
        />
      </div>

      {/* The card's title opens the request. The drag listeners sit on the
          card around it and the sensor only activates past 4px of movement
          (see `sensors` below), so a click that does not move is a click and
          a press that does is a drag — the link needs no special handling to
          stay draggable, and dragging needs none to stay clickable. */}
      <Link
        to={`/requests/${card.id}`}
        className="mt-0.5 block line-clamp-2 fs-12.5 font-semibold text-ink hover:text-accent hover:underline"
      >
        {card.title}
      </Link>

      {/* One line, not a bordered footer with a button in it: hours logged
          against the estimate is the only number a card needs, and logging
          them is done on the request itself, where the work is discussed. */}
      <div className="mt-1 flex items-baseline justify-between gap-2">
        <span className="truncate fs-10.5 text-muted">
          {card.projectName ?? 'No project'} · {card.ownerName ?? 'Unassigned'}
        </span>
        <span className="numeric flex-none fs-10.5 text-muted">
          {card.approxHours > 0 ? (
            <>
              {formatHours(card.loggedHours)}
              <span className="text-faint"> / {formatHours(card.approxHours)}</span>
            </>
          ) : card.loggedHours > 0 ? (
            formatHours(card.loggedHours)
          ) : (
            '—'
          )}
        </span>
      </div>
    </>
  );
}

const CARD_CLASS = 'rounded-lg border border-line bg-base p-2.5';

/**
 * Not viewing mode's card with `useSortable({ disabled })`, which is what this
 * was: a disabled sortable is still a sortable, still subscribed to dnd-kit's
 * context, still re-rendered on every pointer frame of a drag that cannot
 * happen. Reading the plan is the common case and should cost nothing.
 */
const StaticCard = memo(function StaticCard({
  card,
  workspaceId,
}: {
  readonly card: BoardCard;
  readonly workspaceId: string;
}) {
  return (
    <div className={CARD_CLASS}>
      <CardFace card={card} workspaceId={workspaceId} />
    </div>
  );
});

/**
 * `memo` because dnd-kit's context updates on every pointer move during a
 * drag, and without it every card on the board re-renders for every frame of
 * one card moving. The props are all primitives or the card row itself, which
 * is a stable object out of the query cache between fetches, so the comparison
 * is cheap and almost always answers "no change".
 */
const DraggableCard = memo(function DraggableCard({
  card,
  workspaceId,
}: {
  readonly card: BoardCard;
  readonly workspaceId: string;
}) {
  const sortable = useSortable({ id: card.id });

  const style = {
    transform: CSS.Transform.toString(sortable.transform),
    transition: sortable.transition,
  };

  return (
    <div
      ref={sortable.setNodeRef}
      style={style}
      className={cx(
        CARD_CLASS,
        'cursor-grab touch-none active:cursor-grabbing',
        sortable.isDragging && 'z-10 opacity-50 shadow-lg',
      )}
      {...sortable.attributes}
      {...sortable.listeners}
    >
      <CardFace card={card} workspaceId={workspaceId} />
    </div>
  );
});

const BoardCell = memo(function BoardCell({
  lane,
  column,
  cards,
  draggable,
  divider,
  workspaceId,
}: {
  readonly lane: BoardLane;
  readonly column: BoardColumn;
  readonly cards: readonly BoardCard[];
  readonly draggable: boolean;
  /** False for the first column, which has nothing to its left to divide from. */
  readonly divider: boolean;
  readonly workspaceId: string;
}) {
  const droppable = useDroppable({ id: cellId(lane, column) });
  const ids = useMemo(() => cards.map((card) => card.id), [cards]);

  return (
    <div
      ref={droppable.setNodeRef}
      style={{ maxHeight: CELL_MAX_HEIGHT }}
      className={cx(
        'flex min-h-[3.5rem] flex-col gap-1.5 overflow-y-auto rounded-lg p-1.5 outline-1 outline-offset-[-1px]',
        // A dotted rule down the left of every column but the first.
        //
        // Six columns of white cards on a tinted band gave nothing but the
        // gap to say where one column ended and the next began, and a card
        // near a cell edge read as ambiguous — which column a card is in is
        // the single most important thing this layout says, since the column
        // is what the client sees as status.
        //
        // Dotted rather than solid, and on the cell rather than as its own
        // grid track, so it stays quieter than the lane borders (which carry
        // real meaning) and lines up across both bands automatically — every
        // band renders the same gridTemplateColumns.
        divider && 'border-l border-dotted border-line-strong',
        droppable.isOver ? 'bg-base/70 outline-accent' : 'outline-transparent',
      )}
    >
      {draggable ? (
        <SortableContext items={ids} strategy={verticalListSortingStrategy}>
          {cards.map((card) => (
            <DraggableCard key={card.id} card={card} workspaceId={workspaceId} />
          ))}
        </SortableContext>
      ) : (
        cards.map((card) => <StaticCard key={card.id} card={card} workspaceId={workspaceId} />)
      )}
      {/* Fills the remainder of the cell so a drop below the last card (or
          into a wholly empty cell) still lands on this droppable — without
          this, the droppable region would shrink to exactly the height of its
          cards and a drop 20px below the last one would miss it entirely. */}
      <div className="min-h-[0.5rem] flex-1" />
    </div>
  );
});

function LaneBand({
  lane,
  label,
  sub,
  byCell,
  draggable,
  workspaceId,
}: {
  readonly lane: BoardLane;
  readonly label: string;
  readonly sub: string | null;
  readonly byCell: ReadonlyMap<string, BoardCard[]>;
  readonly draggable: boolean;
  readonly workspaceId: string;
}) {
  const tone = LANE_TONES[lane];

  return (
    <section className={cx('mb-3 rounded-xl border p-2', tone.band)}>
      <header className="mb-2 flex flex-wrap items-baseline gap-x-2.5 gap-y-1 px-1">
        <span
          className={cx(
            'rounded-full px-2 py-0.5 fs-10.5 font-bold tracking-[0.04em] whitespace-nowrap uppercase',
            tone.chip,
          )}
        >
          {label}
        </span>
        {sub !== null && <span className="fs-11 text-muted">{sub}</span>}
      </header>

      <div className="grid gap-2" style={{ gridTemplateColumns: GRID_COLUMNS }}>
        {BOARD_COLUMNS.map((column, index) => (
          <BoardCell
            key={cellId(lane, column)}
            lane={lane}
            column={column}
            cards={byCell.get(cellId(lane, column)) ?? []}
            draggable={draggable}
            divider={index > 0}
            workspaceId={workspaceId}
          />
        ))}
      </div>
    </section>
  );
}

function items(count: number): string {
  return `${count} ${count === 1 ? 'item' : 'items'}`;
}

/** What the sprint band says about itself: the week's own name, where it is in
 * its life, and how much is in it. */
function sprintSub(sprint: BoardSprint, count: number): string {
  return `${TIMELINE_STATE_LABEL[sprint.state]} · ${sprintRange(sprint.startsOn, sprint.endsOn)} · ${items(count)}`;
}

export function DeliveryBoard({
  workspaceId,
  editing,
}: {
  readonly workspaceId: string;
  readonly editing: boolean;
}): ReactElement {
  const canMove = useCapability('board.write');
  // Editing is the intent, the capability is the permission, and a drag needs
  // both: the layout toggle must not appear to grant something a role does not.
  const draggable = editing && canMove;

  const query = useQuery(boardQueryOptions(workspaceId));
  const moveMutation = useBoardMove(workspaceId);

  const cards = useMemo(() => query.data?.cards ?? [], [query.data]);
  const sprint = query.data?.sprint ?? null;
  const outsideThePlan = query.data?.outsideThePlan ?? 0;
  const reserveHours = query.data?.reserveHours ?? 0;
  const hotfixHours = query.data?.hotfixHours ?? 0;

  const bands = useMemo(() => {
    const counts = new Map<BoardLane, number>();
    for (const card of cards) counts.set(card.lane, (counts.get(card.lane) ?? 0) + 1);

    return LANES.map((lane) => {
      const count = counts.get(lane) ?? 0;
      if (lane === 'sprint') {
        return {
          lane,
          label: sprint?.name ?? LANE_LABELS.sprint,
          sub: sprint === null ? null : sprintSub(sprint, count),
        };
      }
      // The reserve is the only number that makes the hotfix band mean
      // anything: "2 items" is a list, "2h of 7h reserved" is a week still
      // intact or a week already gone.
      return {
        lane,
        label: LANE_LABELS[lane],
        sub: `${items(count)} · ${formatHours(hotfixHours)} of ${formatHours(reserveHours)} reserved`,
      };
    });
  }, [cards, sprint, hotfixHours, reserveHours]);

  const [activeId, setActiveId] = useState<string | null>(null);

  const byCell = useMemo(() => {
    const map = new Map<string, BoardCard[]>();
    for (const card of cards) {
      const key = cellId(card.lane, card.boardColumn);
      const list = map.get(key);
      if (list) list.push(card);
      else map.set(key, [card]);
    }
    return map;
  }, [cards]);

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const handleDragStart = useCallback((event: DragStartEvent) => setActiveId(String(event.active.id)), []);

  const handleDragEnd = (event: DragEndEvent) => {
    setActiveId(null);
    const { active, over } = event;
    if (over === null) return;

    const movedId = String(active.id);
    const moved = cards.find((card) => card.id === movedId);
    if (!moved) return;

    const overId = String(over.id);
    let targetLane: BoardLane;
    let targetColumn: BoardColumn;
    let overCardId: string | null = null;

    if (overId.startsWith('cell::')) {
      const [, lane, column] = overId.split('::');
      targetLane = lane as BoardLane;
      targetColumn = column as BoardColumn;
    } else {
      const overCard = cards.find((card) => card.id === overId);
      if (!overCard) return;
      targetLane = overCard.lane;
      targetColumn = overCard.boardColumn;
      overCardId = overCard.id;
    }

    const cellCards = byCell.get(cellId(targetLane, targetColumn)) ?? [];
    const withoutMoved = cellCards.filter((card) => card.id !== movedId);
    const insertAt =
      overCardId === null ? withoutMoved.length : withoutMoved.findIndex((card) => card.id === overCardId);
    const resolvedInsertAt = insertAt === -1 ? withoutMoved.length : insertAt;

    // Highest-rank-first list: the card above the insertion point bounds the
    // new rank from above (afterId), the card below bounds it from below
    // (beforeId) — identical convention to BacklogPage's handleDragEnd.
    const after = withoutMoved[resolvedInsertAt - 1];
    const before = withoutMoved[resolvedInsertAt];

    if (moved.boardColumn === targetColumn && after?.id === moved.id) return;

    moveMutation.mutate({
      movedId,
      column: targetColumn,
      afterId: after?.id,
      beforeId: before?.id,
    });
  };

  const activeCard = activeId !== null ? cards.find((card) => card.id === activeId) : undefined;

  if (query.isPending) return <p className="fs-12 text-muted">Loading…</p>;
  if (query.error !== null) {
    return <EmptyState title="Could not load delivery" description={query.error.message} />;
  }

  return (
    <div>
      {/* Six columns need more width than a laptop has. Scrolling sideways in
          here keeps the rest of the Plan screen at the page's own width
          instead of making every layout on it as wide as this one. */}
      <div className="overflow-x-auto">
        <div className="min-w-[87.5rem]">
          <div className="mb-2 grid gap-2" style={{ gridTemplateColumns: GRID_COLUMNS }}>
            {BOARD_COLUMNS.map((column, index) => (
              <div
                key={column}
                className={cx(
                  'flex items-baseline justify-between gap-2 rounded-lg bg-surface-3 px-2.5 py-1.5 fs-11 font-bold tracking-[0.04em] text-muted uppercase',
                  // Same rule as the cells below, so the guideline starts at
                  // the column's own name rather than beginning halfway down.
                  index > 0 && 'border-l border-dotted border-line-strong',
                )}
              >
                <span>{COLUMN_LABELS[column]}</span>
                {/* Done shows recent work only — see board.service.ts's
                    DONE_VISIBLE_DAYS. Said here, next to the column it
                    bounds, with the way to the rest. */}
                {column === 'done' && (
                  <Link
                    to="/completed"
                    className="fs-10 font-semibold tracking-normal normal-case text-accent hover:underline"
                  >
                    Recent · see all
                  </Link>
                )}
              </div>
            ))}
          </div>

          <DndContext
            sensors={sensors}
            collisionDetection={closestCorners}
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            {bands.map((band) =>
              band.lane === 'sprint' && sprint === null ? (
                // No start button: starting a sprint is a planning decision
                // made against capacity and scope, which is the other layout's
                // whole subject. Offering it from here would be a one-click
                // commitment taken without any of that in front of you.
                <EmptyState
                  key={band.lane}
                  className="rounded-xl border border-line bg-surface-2"
                  title="No sprint is running"
                  description="Lock and start a sprint from the Planning view. Hotfixes still show below."
                />
              ) : (
                <LaneBand
                  key={band.lane}
                  lane={band.lane}
                  label={band.label}
                  sub={band.sub}
                  byCell={byCell}
                  draggable={draggable}
                  workspaceId={workspaceId}
                />
              ),
            )}

            <DragOverlay>
              {activeCard !== undefined ? (
                <div className="w-[12.5rem] rounded-lg border border-line-strong bg-base p-2.5 fs-12.5 font-semibold text-ink shadow-lg">
                  {activeCard.title}
                </div>
              ) : null}
            </DragOverlay>
          </DndContext>
        </div>
      </div>

      {/* A line, not a band. This number is meant to be zero, and a lane that
          is empty most weeks is a lane people stop reading — where a sentence
          that only appears when something is wrong keeps its weight. */}
      {outsideThePlan > 0 && (
        <p className="flex flex-wrap items-center gap-x-1.5 gap-y-1 rounded-control border border-warn-border bg-warn-tint px-2.5 py-1.5 fs-12 text-warn">
          <TriangleAlert size={13} aria-hidden />
          <span>
            {outsideThePlan} {outsideThePlan === 1 ? 'request is' : 'requests are'} in progress outside any
            sprint
          </span>
          <Link to="/backlog" className="font-semibold underline">
            Open the backlog
          </Link>
        </p>
      )}

      {moveMutation.error !== null && (
        <p role="alert" className="mt-3 fs-11 text-danger">
          {moveMutation.error.message}
        </p>
      )}
    </div>
  );
}
